import { FieldArray, Form, Formik } from "formik";
import React, { useState } from "react";
import {
  DefaultButton,
  DefaultPrimaryButton,
  InputError,
  InputWithLabel,
} from "../../components/micro";
import Breadcrumb from "../../components/common/Breadcrumb";
import * as Yup from "yup";
import { useLocation, useNavigate } from "react-router-dom";
import FileUploadChoose from "../../components/common/FileUploadChoose";
import { DeleteIcon } from "../../assets/svg";
import { TimePicker } from "@mui/x-date-pickers/TimePicker";
import dayjs from "dayjs";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import { uploadImage } from "../../redux/uploadFile/slice";
import { createMovement, editMovement } from "../../redux/movement/slice";
import ConfirmationModel from "../../components/common/ConfirmationModel";

const AddEditMovement = () => {
  const location = useLocation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [isEditing, setIsEditing] = useState(false);
  const movementData = location?.state?.movement;
  const isEditMode = location?.state?.mode;
  const [isUploading, setIsUploading] = useState(false);
  const [formValues, setFormValues] = useState(null);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const handleEditClick = () => setIsEditing(true);
  const handleCancelClick = () => navigate("/movement");

  // file upload

  const handleFileChange = async (event, setFieldValue, type, index = null) => {
    const files = Array.from(event.target.files);
    if (!files.length) return;

    const temp = files[0];
    const localUrl = URL.createObjectURL(temp);

    // Show instant local preview
    if (type === "movement") {
      setFieldValue("image", localUrl);
    } else if (type === "sub" && index !== null) {
      setFieldValue(`subMovements.${index}.image`, localUrl);
    }

    // Upload to server
    setIsUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", temp);

      const res = await dispatch(uploadImage(formData));
      if (uploadImage.fulfilled.match(res)) {
        const { url, key } = res.payload.data;

        // Update with actual server image & key
        if (type === "movement") {
          setFieldValue("image", url); // server URL for preview
          setFieldValue("imageKey", key); // store key for API payload
        } else if (type === "sub" && index !== null) {
          setFieldValue(`subMovements.${index}.image`, url);
          setFieldValue(`subMovements.${index}.imageKey`, key);
        }
      } else {
        toast.error("Upload failed. Please try again.");
      }
    } catch (e) {
      toast.error("Upload failed. Please try again.");
    } finally {
      setIsUploading(false);
    }
  };

  // get image date

  const handleConfirm = () => {
    setIsLoading(true);

    const payload = {
      movementName: formValues?.name,
      title: formValues?.title,
      description: formValues?.description,
      selecttime: formValues?.totalMinutes,
      calories: formValues?.calories,
      impact: formValues?.impact,
      equipment: formValues?.equipment,
      coverImage: formValues?.imageKey, // send only the key to API
      subMovements: formValues?.subMovements?.map((sub) => ({
        subtitle: sub.title,
        subtime: sub.time,
        submovementImage: sub.imageKey,
      })),
    };

    const action = movementData?._id
      ? editMovement({ _id: movementData._id, ...payload })
      : createMovement(payload);

    dispatch(action)
      .then((res) => {
        if (
          res.type === "createMovement/fulfilled" ||
          res.type === "editMovement/fulfilled"
        ) {
          navigate("/movement");
        }
      })
      .finally(() => {
        setIsLoading(false);
        setShowConfirmationModal(false);
      });
  };

  return (
    <>
      <div className="space-y-5">
        <div>
          <h6 className="font-Montserrat font-medium text-xl mb-3">
            {isEditMode === "edit" ? "Edit Movement" : "Create Movement"}
          </h6>
          <Breadcrumb
            breadCrumbParent={"Home"}
            breadCrumbParent2={"Movement"}
            breadCrumbActive={
              isEditMode === "edit" ? "Edit Movement" : "Create Movement"
            }
            breadCrumbTitleKey={"/"}
            classes=""
          />
        </div>
        <div className="bg-white shadow-md rounded-lg p-6">
          <Formik
            initialValues={{
              name: movementData?.movementName || "",
              title: movementData?.title || "",
              description: movementData?.description || "",
              totalMinutes: movementData?.selecttime || "",
              calories: movementData?.calories || "",
              impact: movementData?.impact || "",
              equipment: movementData?.equipment || "",
              image: movementData?.coverImage || "", // for preview
              imageKey: movementData?.coverImageKey || "", // for API
              subMovements:
                movementData?.subMovements?.map((sub) => ({
                  title: sub.subtitle || "",
                  time: sub.subtime || "00:00",
                  image: sub.submovementImage || "", // preview
                  imageKey: sub.submovementImageKey || "", // API
                })) || [],
            }}
            validationSchema={Yup.object().shape({
              name: Yup.string()
                .trim()
                .nullable()
                .required("Please enter movement name"),
              title: Yup.string()
                .trim()
                .nullable()
                .required("Please enter title"),
              description: Yup.string()
                .trim()
                .nullable()
                .required("Please enter description"),
              totalMinutes: Yup.string()
                .matches(/^\d{2}:\d{2}$/, "Must be in HH:mm format")
                .required("Time is required"),
              calories: Yup.number()
                .required("Please enter calories")
                .positive("Must be greater than 0"),
              impact: Yup.string()
                .trim()
                .nullable()
                .required("Please enter impact"),
              equipment: Yup.string()
                .trim()
                .nullable()
                .required("Please enter equipment"),
              image: Yup.mixed().required("Image is required"),
              subMovements: Yup.array().of(
                Yup.object().shape({
                  title: Yup.string()
                    .trim()
                    .nullable()
                    .required("Please enter subtitle"),
                  time: Yup.string().required("Please enter time"),
                  image: Yup.mixed()
                    .nullable()
                    .required("Please upload Sub Movement image"),
                })
              ),
            })}
            validateOnMount
            validateOnChange
            validateOnBlur
            onSubmit={(values) => {
              setFormValues(values);
              setShowConfirmationModal(true);
            }}
          >
            {({
              handleChange,
              handleBlur,
              values,
              errors,
              touched,
              setFieldValue,
              setFieldTouched,
            }) => (
              <Form>
                <div className="grid grid-cols-2 gap-6 mb-4">
                  <InputWithLabel
                    idFromName="name"
                    name="name"
                    label="Movement Name*"
                    type="text"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.name}
                    errorsText={errors.name}
                    disabled={isEditMode === "edit" && !isEditing}
                    errors={errors.name && touched.name}
                    className={`${
                      errors?.name && touched?.name
                        ? `border border-red`
                        : `border border-borderTheme/80 focus-within:border-themeBtn `
                    } w-full `}
                  />
                  <InputWithLabel
                    idFromName="title"
                    name="title"
                    label="Title*"
                    type="text"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.title}
                    errorsText={errors.title}
                    disabled={isEditMode === "edit" && !isEditing}
                    errors={errors.title && touched.title}
                    className={`${
                      errors?.title && touched?.title
                        ? `border border-red`
                        : `border border-borderTheme/80 focus-within:border-themeBtn `
                    } w-full `}
                  />

                  {/* Time */}
                  <div>
                    <TimePicker
                      label="Select Time*"
                      value={dayjs(values.totalMinutes || "00:00", "mm:ss")}
                      onChange={(newValue) => {
                        const formatted = dayjs(newValue).format("mm:ss");
                        setFieldValue("totalMinutes", formatted);
                        setFieldTouched("totalMinutes", true, false);
                      }}
                      disabled={isEditMode === "edit" && !isEditing}
                      views={["minutes", "seconds"]}
                      autoFocus={false}
                      format="mm:ss"
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          sx: {
                            "& .MuiOutlinedInput-root": {
                              "& fieldset": {
                                borderColor: "#00000066",
                              },
                              "&:hover fieldset": {
                                borderColor: "black",
                              },
                              "&.Mui-focused fieldset": {
                                borderColor: "black !important",
                                borderWidth: "2px",
                              },
                            },
                            "& .MuiInputLabel-root.Mui-focused": {
                              color: "black !important",
                            },
                          },
                        },
                      }}
                    />

                    {errors.totalMinutes && touched.totalMinutes && (
                      <InputError errorTitle={errors.totalMinutes} />
                    )}
                  </div>
                  <InputWithLabel
                    idFromName="calories"
                    name="calories"
                    label="Calories*"
                    type="number"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.calories}
                    errorsText={errors.calories}
                    disabled={isEditMode === "edit" && !isEditing}
                    errors={errors.calories && touched.calories}
                    className={`${
                      errors?.calories && touched?.calories
                        ? `border border-red`
                        : `border border-borderTheme/80 focus-within:border-themeBtn `
                    } w-full `}
                    onFocus={(e) =>
                      e.target.addEventListener(
                        "wheel",
                        function (e) {
                          e.preventDefault();
                        },
                        { passive: false }
                      )
                    }
                  />
                  <InputWithLabel
                    idFromName="impact"
                    name="impact"
                    label="Impact*"
                    type="text"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.impact}
                    errorsText={errors.impact}
                    disabled={isEditMode === "edit" && !isEditing}
                    errors={errors.impact && touched.impact}
                    className={`${
                      errors?.impact && touched?.impact
                        ? `border border-red`
                        : `border border-borderTheme/80 focus-within:border-themeBtn `
                    } w-full `}
                  />
                  <InputWithLabel
                    idFromName="equipment"
                    name="equipment"
                    label="Equipment*"
                    type="text"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.equipment}
                    errorsText={errors.equipment}
                    disabled={isEditMode === "edit" && !isEditing}
                    errors={errors.equipment && touched.equipment}
                    className={`${
                      errors?.equipment && touched?.equipment
                        ? `border border-red`
                        : `border border-borderTheme/80 focus-within:border-themeBtn `
                    } w-full `}
                  />
                </div>
                <div className="my-4 grid grid-cols-2 gap-6">
                  <div className="mt-3">
                    <InputWithLabel
                      idFromName="description"
                      name="description"
                      label="Description*"
                      type="textarea"
                      rows={10}
                      maxWords={150}
                      minWords={3}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      value={values.description}
                      errorsText={errors.description}
                      disabled={isEditMode === "edit" && !isEditing}
                      errors={errors.description && touched.description}
                      className={`${
                        errors?.description && touched?.description
                          ? `border border-red`
                          : `border border-borderTheme/80 focus-within:border-themeBtn `
                      } w-full `}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2 text-gray-dark font-Proxima">
                      Cover Image*
                    </label>
                    <div>
                      <FileUploadChoose
                        className={`border border-borderTheme/80 transition duration-300 ease-in-out ${
                          errors.image && touched.image ? "border-red" : ""
                        }`}
                        disabled={isEditMode === "edit" && !isEditing}
                        uploadedFiles={
                          values.imageKey // <-- only true when upload succeeded
                            ? [{ url: values.image }]
                            : [] // no delete button until API success
                        }
                        filemsg="Upload JPG, PNG, JPEG file"
                        accept="image/*"
                        handleFileChange={(event) =>
                          handleFileChange(event, setFieldValue, "movement")
                        }
                        onDelete={() => {
                          setFieldValue("image", null);
                          setFieldValue("imageKey", null); // RESET here
                        }}
                      />
                      {errors.image && touched.image ? (
                        <InputError errorTitle={errors.image} />
                      ) : null}
                    </div>
                  </div>
                </div>
                <FieldArray name="subMovements">
                  {({ push, remove }) => (
                    <>
                      {values.subMovements.map((sub, index) => (
                        <>
                          <div
                            key={index}
                            className=" relative border border-borderTheme/80 p-8 rounded-md mb-4"
                          >
                            {/* Delete Icon */}
                            {isEditMode !== "edit" || isEditing ? (
                              <button
                                type="button"
                                onClick={() => remove(index)}
                                disabled={isEditMode === "edit" && !isEditing}
                                className="absolute top-1 right-1"
                              >
                                <DeleteIcon />
                              </button>
                            ) : null}
                            <div className="relative grid grid-cols-2 gap-4 mb-4">
                              {/* Title */}
                              <InputWithLabel
                                name={`subMovements.${index}.title`}
                                label="Sub Title*"
                                value={sub.title}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                errorsText={
                                  errors?.subMovements?.[index]?.title
                                }
                                disabled={isEditMode === "edit" && !isEditing}
                                idFromName={`subMovements.${index}.title`}
                                errors={
                                  errors?.subMovements?.[index]?.title &&
                                  touched?.subMovements?.[index]?.title
                                }
                                className={`${
                                  errors?.subMovements?.[index]?.title &&
                                  touched?.subMovements?.[index]?.title
                                    ? `border border-red`
                                    : `border border-borderTheme/80 focus-within:border-themeBtn `
                                } w-full `}
                              />

                              {/* Time */}
                              <div>
                                <TimePicker
                                  label="Sub Time*"
                                  value={dayjs(
                                    values.subMovements[index]?.time || "00:00",
                                    "mm:ss"
                                  )}
                                  onChange={(newValue) => {
                                    const formatted =
                                      dayjs(newValue).format("mm:ss");
                                    setFieldValue(
                                      `subMovements.${index}.time`,
                                      formatted,
                                      true
                                    );
                                    setFieldTouched(
                                      `subMovements.${index}.time`,
                                      true,
                                      false
                                    );
                                  }}
                                  className={`${
                                    errors?.subMovements?.[index]?.time &&
                                    touched?.subMovements?.[index]?.time
                                      ? `border border-red`
                                      : `border border-borderTheme/80 focus-within:border-themeBtn `
                                  } w-full `}
                                  disabled={isEditMode === "edit" && !isEditing}
                                  views={["minutes", "seconds"]}
                                  format="mm:ss"
                                />
                                {errors?.subMovements?.[index]?.time &&
                                touched?.subMovements?.[index]?.time ? (
                                  <InputError
                                    errorTitle={errors.subMovements[index].time}
                                  />
                                ) : null}
                              </div>
                            </div>
                            <div className="mb-4 mt-4">
                              <label className="block text-sm font-medium mb-2 text-gray-dark font-Proxima">
                                Sub Movement Cover Image*
                              </label>
                              <div>
                                <FileUploadChoose
                                  className={`border border-borderTheme/80 transition duration-300 ease-in-out ${
                                    errors.subMovements?.[index]?.image &&
                                    touched.subMovements?.[index]?.image
                                      ? "border-red"
                                      : ""
                                  }`}
                                  disabled={isEditMode === "edit" && !isEditing}
                                  uploadedFiles={
                                    values.subMovements[index].imageKey // show delete only after API upload success
                                      ? [
                                          {
                                            url: values.subMovements[index]
                                              .image,
                                          },
                                        ]
                                      : []
                                  }
                                  filemsg="Upload JPG, PNG, JPEG file"
                                  accept="image/*"
                                  handleFileChange={(event) =>
                                    handleFileChange(
                                      event,
                                      setFieldValue,
                                      "sub",
                                      index
                                    )
                                  }
                                  onDelete={() => {
                                    setFieldValue(
                                      `subMovements.${index}.image`,
                                      null
                                    );
                                    setFieldValue(
                                      `subMovements.${index}.imageKey`,
                                      null
                                    );
                                  }}
                                />
                                {errors?.subMovements?.[index]?.image &&
                                touched?.subMovements?.[index]?.image ? (
                                  <InputError
                                    errorTitle={
                                      errors.subMovements[index].image
                                    }
                                  />
                                ) : null}
                              </div>
                            </div>
                          </div>
                        </>
                      ))}

                      <DefaultPrimaryButton
                        type="button"
                        value={"+ Add Sub Movement"}
                        addclass={` ${
                          isEditMode === "edit" && !isEditing
                            ? "disabled text-textGrey border border-dashed"
                            : "text-themeBtn border-themeBtn hover:bg-themeBtn hover:text-white"
                        }`}
                        onClick={() =>
                          push({
                            title: "",
                            time: "",
                            image: null,
                          })
                        }
                        disabled={isEditMode === "edit" && !isEditing}
                      />
                    </>
                  )}
                </FieldArray>

                <div className="flex justify-end gap-3 mt-3">
                  <DefaultPrimaryButton
                    type="button"
                    value={isEditMode === "edit" ? "Back" : "Cancel"}
                    onClick={handleCancelClick}
                  />
                  {isEditMode === "edit" ? (
                    isEditing ? (
                      <>
                        <DefaultButton
                          type="submit"
                          value="Save"
                          onClick={(e) => {}}
                        />
                      </>
                    ) : (
                      <DefaultButton
                        value="Edit"
                        onClick={handleEditClick}
                        type="submit"
                      />
                    )
                  ) : (
                    <>
                      <DefaultButton type="submit" value="confirm" />
                    </>
                  )}
                </div>
              </Form>
            )}
          </Formik>
        </div>
        {showConfirmationModal && (
          <ConfirmationModel
            confirmText={isEditMode === "edit" ? "Edit" : "Create"}
            onClose={() => setShowConfirmationModal(false)}
            onConfirm={handleConfirm}
            isLoading={isLoading}
          />
        )}
      </div>
    </>
  );
};

export default AddEditMovement;
